/**
 * PresenterScreen: PRESENTER 모드 — 발표자 + 청중 레이아웃.
 *
 * v1 원본: RenewalComponents/SVContent/PresenterScreen/
 *          PresenterScreenContainer.js + PresenterScreenPresenter.js
 * v2 변환: 단일 함수형 컴포넌트
 *
 * 기능:
 * - 발표자 영역 (최대 2명) + 청중 영역 (수평 스크롤)
 * - 드래그 앤 드롭: 청중 → 발표자 영역으로 끌어서 배치 (order 기반)
 * - 발표자 우선순위: doubleClick > isShareClick > masterPinnedUserIds > pinnedUserIds > mainUser > dominantSpeaker > 첫 참석자
 * - 더블클릭: 특정 참여자를 강제 발표자로 설정
 * - 화면 자동 전환: 발표자 1명 + 고정 없음일 때만 활성화
 * - 드래그 시 마스터 핀이 2개면 차단 + 스낵바 메시지
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';
import { mainUserActions } from '@/store/slices/mainUserSlice';
import { toastActions } from '@/store/slices/toastSlice';
import { isMasterUser } from '@/store/slices/masterSlice';
import LUXDialog from '@/components/Dialog/LUXDialog';
import { getConferenceManager } from '@/conference/conferenceManagerRef';
import { CONFERENCE_COMMANDS } from '@/conference/commands';
import VideoPanel from '../SubVideo/VideoPanel';
import MainVideoPanel from './MainVideoPanel';
import type { Participant } from '@/store/slices/participantsSlice';
import type { ScreenUser } from '@/types/components';
import { toPinKey, parsePinKey } from '@/utils/pinUtils';
import { isRecordingBot } from '@/utils/recordingBot';
import { isRecorderMode } from '@/utils/recorderMode';
import { getSyncedNow } from '@/utils/timeOffset';

interface PresenterScreenProps {
  localUserId: string;
  onSelectParticipant?: (userId: string | null) => void;
}

const MAX_PRESENTER_COUNT = 2;

const PresenterScreen: React.FC<PresenterScreenProps> = ({ localUserId, onSelectParticipant }) => {
  const dispatch = useAppDispatch();
  const participantIds = useAppSelector((s) => s.participants.participantIds);
  const participantsMap = useAppSelector((s) => s.participants.participants);
  const mainUser = useAppSelector((s) => s.mainUser);
  const activeSpeakers = useAppSelector((s) => s.audioIndicator.activeSpeakers);
  const presenterOverrideUserId = useAppSelector((s) => s.ui.presenterOverrideUserId);
  const isShareClick = useAppSelector((s) => s.ui.isShareClick);
  const isSharing = useAppSelector((s) => s.screenShare.isSharing);
  const localUserName = useAppSelector((s) => s.local.userName);
  const localNickname = useAppSelector((s) => s.local.nickname);
  const localWehagoId = useAppSelector((s) => s.local.wehagoId);
  const masterUsers = useAppSelector((s) => s.master.masterUsers);
  const isAutoScreenSwitch = useAppSelector((s) => s.ui.isAutoScreenSwitch);
  const screenSort = useAppSelector((s) => s.ui.screenSort);
  const speakerOrder = useAppSelector((s) => s.audioIndicator.speakerOrder);

  const [presenterPreviewIndex, setPresenterPreviewIndex] = useState<number | null>(null);
  const isDoubleClick = presenterOverrideUserId !== null;
  const doubleClickUserId = presenterOverrideUserId;
  const [isExpanded, setIsExpanded] = useState(false);
  const presenterRef = useRef<HTMLDivElement>(null);

  // 클릭 후 2초간 임시 점유 (자동 전환 모드에서 클릭 시)
  const CLICK_COOLDOWN_MS = 2000;
  const clickCooldownRef = useRef(false);
  const [isInClickCooldown, setIsInClickCooldown] = useState(false);

  const isLocalMaster = isMasterUser(masterUsers, localWehagoId);

  const showSnackbar = useCallback((type: 'success' | 'error' | 'info', message: string) => {
    dispatch(toastActions.showSnackbar({ type, message }));
  }, [dispatch]);

  // ─── 전체 참여자 목록 (화면 공유 가상 타일 포함) ───────────────────────────
  const allUsers = useMemo(() => {
    // recorder 모드에서는 자기 자신(아바타)을 녹화 화면에 넣지 않는다.
    const list: ScreenUser[] = isRecorderMode() ? [] : [{ id: localUserId, isLocal: true }];
    // 로컬 화면 공유 타일
    if (!isRecorderMode() && isSharing) {
      list.push({ id: localUserId, isLocal: true, isScreenShare: true });
    }
    participantIds.forEach((id) => {
      const p = participantsMap[id];
      if (!p) return;
      if (p.isHidden || p.properties?.features_jigasi) return;
      if (isRecordingBot(p)) return;
      list.push({ id, isLocal: false });
      // 원격 화면 공유 타일
      if (p.tracks.isScreenSharing) {
        list.push({ id, isLocal: false, isScreenShare: true });
      }
    });
    return list;
  }, [participantIds, participantsMap, localUserId, isSharing]);

  // ─── pinnedId → allUsers 엔트리 조회 헬퍼 ──────────────────────────────
  const findUserByPinnedId = useCallback((pinnedId: string, screenShare = false) => {
    return allUsers.find((u) => u.id === pinnedId && (screenShare ? u.isScreenShare : !u.isScreenShare));
  }, [allUsers]);

  // ─── 발표자/청중 분류 (v1 setPresenterAndAudienceList 기반) ──────────────
  const { presenterList, audienceList } = useMemo(() => {
    let presenters: ScreenUser[] = [];
    let isAdd = false;

    // 1. 화면 자동 전환 ON + 현재 발언자 + 핀 없음 + 클릭 쿨다운 아님 → 발언자 우선
    if (
      isAutoScreenSwitch
      && activeSpeakers.length > 0
      && mainUser.pinnedUserIds.length === 0
      && mainUser.masterPinnedUserIds.length === 0
      && !isInClickCooldown
    ) {
      const speakerId = activeSpeakers[0];
      const user = findUserByPinnedId(speakerId);
      if (user) {
        isAdd = true;
        presenters.push(user);
        // 화면 공유 중이면 공유화면 타일도 함께 추가 (카메라 + 공유화면 2개 노출)
        const screenTile = findUserByPinnedId(speakerId, true);
        if (screenTile) presenters.push(screenTile);
      }
    }

    // 2. 더블클릭 강제 지정 (사용자가 특정 타일 선택 → 단일 표시)
    if (!isAdd && isDoubleClick && doubleClickUserId) {
      const { userId: overrideId, isScreenShare: overrideSS } = parsePinKey(doubleClickUserId);
      const user = findUserByPinnedId(overrideId, overrideSS);
      if (user) {
        isAdd = true;
        presenters.push(user);
      }
    }

    // 3. 화면공유 클릭 (mainUser + screen 조합)
    if (!isAdd && isShareClick) {
      const mainId = mainUser.mainUserId ?? mainUser.pinnedUserId;
      if (mainId) {
        const user = findUserByPinnedId(mainId);
        if (user) {
          isAdd = true;
          presenters.push(user);
          // 화면 공유 타일이 있으면 함께 추가
          const screenTile = findUserByPinnedId(mainId, true);
          if (screenTile) presenters.push(screenTile);
        }
      }
    }

    // 4. 마스터 고정 + 개인 고정 합산 (최대 2명)
    //    마스터 핀 우선, 남은 슬롯에 개인 핀 채움
    if (!isAdd && (mainUser.masterPinnedUserIds.length > 0 || mainUser.pinnedUserIds.length > 0)) {
      // 마스터 핀 먼저
      mainUser.masterPinnedUserIds.forEach((pinnedId) => {
        const { userId: uid, isScreenShare: ss } = parsePinKey(pinnedId);
        const user = findUserByPinnedId(uid, ss);
        if (user) {
          isAdd = true;
          presenters.push(user);
        }
      });
      // 남은 슬롯에 개인 핀 채움
      mainUser.pinnedUserIds.forEach((pinnedId) => {
        if (presenters.length >= MAX_PRESENTER_COUNT) return;
        const { userId: uid, isScreenShare: ss } = parsePinKey(pinnedId);
        // 이미 들어간 동일 타일(id+isScreenShare) 중복 제외
        if (presenters.some((p) => p.id === uid && p.isScreenShare === ss)) return;
        const user = findUserByPinnedId(uid, ss);
        if (user) {
          isAdd = true;
          presenters.push(user);
        }
      });
    }

    // 5. 화면 공유 타일이 있으면 발표자에 추가
    if (!isAdd) {
      const screenShareTile = allUsers.find((u) => u.isScreenShare);
      if (screenShareTile) {
        presenters.push(screenShareTile);
        isAdd = true;
      }
    }

    // 6. 화면 자동 전환 ON + 현재 발언자 (더블클릭/핀 없을 때만)
    if (!isAdd && isAutoScreenSwitch && activeSpeakers.length > 0) {
      const speakerId = activeSpeakers[0];
      const user = findUserByPinnedId(speakerId);
      if (user) {
        isAdd = true;
        presenters.push(user);
        // 화면 공유 중이면 공유화면 타일도 함께 추가 (카메라 + 공유화면 2개 노출)
        const screenTile = findUserByPinnedId(speakerId, true);
        if (screenTile) presenters.push(screenTile);
      }
    }

    // 7. mainUserId (dominant speaker로 설정됨)
    if (!isAdd && mainUser.mainUserId) {
      const user = findUserByPinnedId(mainUser.mainUserId);
      if (user) {
        isAdd = true;
        presenters.push(user);
      }
    }

    // 8. pinnedUserId (단일 핀 — 기존 호환)
    if (!isAdd && mainUser.pinnedUserId) {
      const user = findUserByPinnedId(mainUser.pinnedUserId);
      if (user) {
        isAdd = true;
        presenters.push(user);
      }
    }

    // 9. 발언 중인 사용자 (자동 전환 OFF일 때 폴백)
    if (!isAdd && activeSpeakers.length > 0) {
      const speakerId = activeSpeakers[0];
      const user = findUserByPinnedId(speakerId);
      if (user) {
        isAdd = true;
        presenters.push(user);
      }
    }

    // 9. 첫 번째 참석자
    if (!isAdd && allUsers.length > 0) {
      const user = allUsers.find((u) => !u.isScreenShare);
      if (user) presenters.push(user);
    }

    // 최대 2명 제한, 중복 제거 (id+isScreenShare 기준)
    presenters = presenters
      .filter((u, i, self) => i === self.findIndex((o) => o.id === u.id && o.isScreenShare === u.isScreenShare))
      .slice(0, MAX_PRESENTER_COUNT);

    const presenterKeys = new Set(presenters.map((p) => p.isScreenShare ? `${p.id}-screen` : p.id));
    const audience = allUsers.filter((u) => {
      const key = u.isScreenShare ? `${u.id}-screen` : u.id;
      return !presenterKeys.has(key);
    });

    // ─── 청중 정렬 (screenSort) ──────────────────────────────────────────
    if (screenSort === 'SPEAKER' && speakerOrder.length > 0) {
      audience.sort((a, b) => {
        const ai = speakerOrder.indexOf(a.id);
        const bi = speakerOrder.indexOf(b.id);
        const aSpeaker = ai !== -1 ? ai : Infinity;
        const bSpeaker = bi !== -1 ? bi : Infinity;
        return aSpeaker - bSpeaker;
      });
    } else if (screenSort === 'NAME') {
      const localItems = audience.filter((u) => u.isLocal);
      const remoteItems = audience.filter((u) => !u.isLocal);
      remoteItems.sort((a, b) => {
        const pA = participantsMap[a.id];
        const pB = participantsMap[b.id];
        const nameA = pA?.displayName || pA?.nickname || '';
        const nameB = pB?.displayName || pB?.nickname || '';
        return nameA.localeCompare(nameB, 'ko');
      });
      return { presenterList: presenters, audienceList: [...localItems, ...remoteItems] };
    }

    return { presenterList: presenters, audienceList: audience };
  }, [allUsers, mainUser, activeSpeakers, isShareClick, isDoubleClick, doubleClickUserId, findUserByPinnedId, isAutoScreenSwitch, isInClickCooldown, screenSort, speakerOrder, participantsMap]);

  // ─── 화면 자동 전환 가능 여부 (v1: isUseAutoScreenSwitch) ────────────────
  // 발표자 1명 (또는 같은 유저의 카메라+공유화면 2개) + 개인/마스터 고정 없음
  const isAutoSwitchPairPresenter = presenterList.length === 2
    && presenterList[0].id === presenterList[1].id
    && presenterList.some((p) => p.isScreenShare)
    && presenterList.some((p) => !p.isScreenShare);
  const isUseAutoScreenSwitch = (presenterList.length === 1 || isAutoSwitchPairPresenter)
    && mainUser.pinnedUserIds.length === 0
    && mainUser.masterPinnedUserIds.length === 0;

  // ─── 자동 전환 모드에서 클릭 시 2초 쿨다운 ──────────────────────────────
  useEffect(() => {
    if (isAutoScreenSwitch && presenterOverrideUserId !== null) {
      // ref를 동기적으로 설정하여 같은 이펙트 사이클에서 클리어 방지
      clickCooldownRef.current = true;
      setIsInClickCooldown(true);
      const timer = setTimeout(() => {
        clickCooldownRef.current = false;
        setIsInClickCooldown(false);
      }, CLICK_COOLDOWN_MS);
      return () => clearTimeout(timer);
    }
  }, [isAutoScreenSwitch, presenterOverrideUserId]);

  // ─── 쿨다운 종료 후 발언자가 있으면 더블클릭 고정 해제 ──────────────────
  useEffect(() => {
    if (isAutoScreenSwitch && !clickCooldownRef.current && activeSpeakers.length > 0 && presenterOverrideUserId !== null) {
      dispatch(uiActions.setPresenterOverrideUserId(null));
    }
  }, [isAutoScreenSwitch, isInClickCooldown, activeSpeakers, presenterOverrideUserId, dispatch]);

  // ─── 자동 전환으로 발언자가 메인에 표시될 때 mainUserId도 갱신 ──────────
  // 발언 종료 후 이전 고정자(B)로 폴백되는 것을 방지
  // + 화면 공유 중인 발언자로 전환 시 isShareClick 설정 (2패널 모드 활성화)
  useEffect(() => {
    if (
      isAutoScreenSwitch
      && activeSpeakers.length > 0
      && mainUser.pinnedUserIds.length === 0
      && mainUser.masterPinnedUserIds.length === 0
    ) {
      const speakerId = activeSpeakers[0];
      if (mainUser.mainUserId !== speakerId) {
        dispatch(mainUserActions.setMainUser(speakerId));
      }
      // 화면 공유 중인 발언자 → 2패널 모드
      // 로컬 유저는 participantsMap에 없으므로 isSharing으로 별도 체크
      const speakerIsScreenSharing = speakerId === localUserId
        ? isSharing
        : (participantsMap[speakerId]?.tracks.isScreenSharing ?? false);
      if (speakerIsScreenSharing && !isShareClick) {
        dispatch(uiActions.setIsShareClick(true));
      }
    }
  }, [isAutoScreenSwitch, activeSpeakers, mainUser.pinnedUserIds, mainUser.masterPinnedUserIds, mainUser.mainUserId, participantsMap, isShareClick, isSharing, localUserId, dispatch]);

  // ─── shareClick 클린업 ────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      dispatch(uiActions.setIsShareClick(false));
    };
  }, [dispatch]);

  // ─── 청중 스크롤 영역 active 클래스 ──────────────────────────────────────
  useEffect(() => {
    const container = document.getElementById('audience_scroll_wrap');
    if (!container) return;
    if (container.scrollWidth > container.clientWidth) {
      container.classList.add('active');
    } else {
      container.classList.remove('active');
    }
  }, [audienceList.length]);

  // ─── 핀 헬퍼 (v1 useScreenPinManager와 동일 구조) ─────────────────────────

  /** 마스터 핀 고정 (브로드캐스트) — pinKey: 'userId' 또는 'userId:screen' */
  const doMasterPin = useCallback((pinKey: string, order?: number) => {
    const { userId, isScreenShare } = parsePinKey(pinKey);
    const p = participantsMap[userId];
    const name = userId === localUserId
      ? (localUserName || localNickname || '')
      : (p?.displayName || p?.nickname || '');

    const currentMasterPinned = mainUser.masterPinnedUserIds;
    let updatedPinned = [...currentMasterPinned];

    if (!updatedPinned.includes(pinKey)) {
      if (typeof order === 'number' && order >= 0 && order <= updatedPinned.length) {
        updatedPinned.splice(order, 0, pinKey);
      } else {
        updatedPinned.push(pinKey);
      }
      updatedPinned = updatedPinned.slice(-MAX_PRESENTER_COUNT);
    }

    if (updatedPinned.length > MAX_PRESENTER_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PRESENTER_COUNT}개까지 고정가능합니다.`);
      return;
    }

    dispatch(mainUserActions.setMasterPinnedUsers(updatedPinned));

    const room = getConferenceManager()?.getRoom?.();
    if (room) {
      // localUserId가 이미 jitsiId이므로 별도 변환 없이 그대로 브로드캐스트
      const jitsiId = room.myUserId();
      room.sendCommand(CONFERENCE_COMMANDS.UPDATE_MASTER_PIN_FIXED, {
        value: jitsiId,
        attributes: {
          masterPinnedUsers: JSON.stringify(updatedPinned),
          userName: name,
          type: 'pin',
          isScreenShare: isScreenShare ? 'true' : 'false',
          actionTimestamp: getSyncedNow().toString(),
        },
      });
    }

    dispatch(uiActions.setScreenMode('PRESENTER'));
    showSnackbar('info', `화면이 고정되었습니다. ${updatedPinned.length}/${MAX_PRESENTER_COUNT}`);
  }, [dispatch, localUserId, localUserName, localNickname, participantsMap, mainUser.masterPinnedUserIds, showSnackbar]);

  /** 개인 핀 고정 — pinKey: 'userId' 또는 'userId:screen' */
  const doUserPin = useCallback((pinKey: string, order?: number) => {
    const currentPinned = mainUser.pinnedUserIds;

    // 이미 고정된 경우 중복 추가 방지
    if (currentPinned.includes(pinKey)) return;

    // 총 핀 수(master + user) 사전 검증
    const allPinned = new Set([
      ...mainUser.masterPinnedUserIds,
      ...currentPinned,
      pinKey,
    ]);
    if (allPinned.size > MAX_PRESENTER_COUNT) {
      showSnackbar('error', `화면은 최대 ${MAX_PRESENTER_COUNT}개까지 고정가능합니다.`);
      return;
    }

    let updatedPinned = [...currentPinned];
    if (typeof order === 'number' && order >= 0 && order <= updatedPinned.length) {
      updatedPinned.splice(order, 0, pinKey);
    } else {
      updatedPinned.push(pinKey);
    }

    dispatch(mainUserActions.setPinnedUsers(updatedPinned));
    dispatch(uiActions.setScreenMode('PRESENTER'));
    const totalPinned = mainUser.masterPinnedUserIds.length + updatedPinned.length;
    showSnackbar('info', `화면이 고정되었습니다. ${totalPinned}/${MAX_PRESENTER_COUNT}`);
  }, [dispatch, mainUser.pinnedUserIds, mainUser.masterPinnedUserIds, showSnackbar]);

  /** 마스터 핀 해제 (브로드캐스트) — pinKey: 'userId' 또는 'userId:screen' */
  const doMasterUnpin = useCallback((pinKey: string) => {
    const { isScreenShare } = parsePinKey(pinKey);
    const nextPinned = mainUser.masterPinnedUserIds.filter((id) => id !== pinKey);
    dispatch(mainUserActions.setMasterPinnedUsers(nextPinned));

    const room = getConferenceManager()?.getRoom?.();
    if (room) {
      const jitsiId = room.myUserId();
      room.sendCommand(CONFERENCE_COMMANDS.UPDATE_MASTER_PIN_FIXED, {
        value: jitsiId,
        attributes: {
          masterPinnedUsers: JSON.stringify(nextPinned),
          userName: '',
          type: 'unpin',
          isScreenShare: isScreenShare ? 'true' : 'false',
          actionTimestamp: getSyncedNow().toString(),
        },
      });
    }

    showSnackbar('info', `화면 고정이 해제되었습니다. ${nextPinned.length}/${MAX_PRESENTER_COUNT}`);
  }, [dispatch, mainUser.masterPinnedUserIds, showSnackbar]);

  /** 개인 핀 해제 — pinKey: 'userId' 또는 'userId:screen' */
  const doUserUnpin = useCallback((pinKey: string) => {
    const nextPinned = mainUser.pinnedUserIds.filter((id) => id !== pinKey);
    dispatch(mainUserActions.setPinnedUsers(nextPinned));
    const totalPinned = mainUser.masterPinnedUserIds.length + nextPinned.length;
    showSnackbar('info', `화면 고정이 해제되었습니다. ${totalPinned}/${MAX_PRESENTER_COUNT}`);
  }, [dispatch, mainUser.pinnedUserIds, mainUser.masterPinnedUserIds, showSnackbar]);

  // ─── 드래그 & 드롭 핸들러 ─────────────────────────────────────────────────
  const handleDragStart = useCallback((e: React.DragEvent, userId: string) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify({ id: userId }));
  }, []);

  const calcPreviewIndex = useCallback((e: React.DragEvent) => {
    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = e.clientX;
    const count = presenterList.length;
    const sectionWidth = rect.width / (count + 1);
    let index = Math.floor((x - rect.left) / sectionWidth);
    if (index < 0) index = 0;
    if (index > count) index = count;
    return index;
  }, [presenterList.length]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setPresenterPreviewIndex(calcPreviewIndex(e));
  }, [calcPreviewIndex]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPresenterPreviewIndex(calcPreviewIndex(e));
  }, [calcPreviewIndex]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if ((e.currentTarget as HTMLElement).contains(e.relatedTarget as Node)) return;
    setPresenterPreviewIndex(null);
  }, []);

  /** 드롭 — v1: 마스터 핀 2개면 차단, 아니면 handlePin(pin, id, { order }) */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    if (!data) { setPresenterPreviewIndex(null); return; }

    try {
      const { id: rawId } = JSON.parse(data);
      if (!rawId) { setPresenterPreviewIndex(null); return; }

      // rawId는 pinKey 형식일 수 있음 ('userId:screen' 또는 'userId')
      const pinKey = rawId;
      const { userId: dragUserId, isScreenShare: dragSS } = parsePinKey(pinKey);

      // 마스터 고정 화면이 2개일 때 드래그 차단 (v1 동일)
      if (mainUser.masterPinnedUserIds.length >= MAX_PRESENTER_COUNT) {
        if (isLocalMaster) {
          showSnackbar('error', '고정된 화면을 해제한 후 다시 시도해주세요.');
        } else {
          showSnackbar('error', '마스터가 화면을 고정하여 변경할 수 없습니다.');
        }
        setPresenterPreviewIndex(null);
        e.dataTransfer.clearData();
        return;
      }

      // 총 핀 수(master + user)가 최대치일 때 드래그 차단
      const allPinned = new Set([
        ...mainUser.masterPinnedUserIds,
        ...mainUser.pinnedUserIds,
      ]);
      if (!allPinned.has(pinKey) && allPinned.size >= MAX_PRESENTER_COUNT) {
        showSnackbar('error', `화면은 최대 ${MAX_PRESENTER_COUNT}개까지 고정가능합니다.`);
        setPresenterPreviewIndex(null);
        e.dataTransfer.clearData();
        return;
      }

      const order = presenterPreviewIndex ?? undefined;

      // 마스터 → "모두에게 / 나에게만" 선택 다이얼로그
      if (isLocalMaster) {
        const p = participantsMap[dragUserId];
        const name = dragUserId === localUserId
          ? (localUserName || localNickname || '')
          : (p?.displayName || p?.nickname || '');

        const dialogMessage = dragSS
          ? `${name}님의 공유 화면을\n모두에게 화면 고정 하시겠습니까?`
          : `${name}님을\n모두에게 화면 고정 하시겠습니까?`;

        LUXDialog.confirm(dialogMessage, {
          type: 'info',
          title: '화면 고정',
          confirmButtonLabel: '모두에게 화면 고정',
          cancelButtonLabel: '나에게만 화면 고정',
          confirmCallback: () => doMasterPin(pinKey, order),
          cancelCallback: () => doUserPin(pinKey, order),
        });
      } else {
        // 비마스터: 개인 핀
        doUserPin(pinKey, order);
      }

      dispatch(uiActions.setIsShareClick(false));
      dispatch(uiActions.setPresenterOverrideUserId(null));
    } catch { /* invalid JSON */ }

    e.dataTransfer.clearData();
    setPresenterPreviewIndex(null);
  }, [dispatch, mainUser.masterPinnedUserIds, isLocalMaster, presenterPreviewIndex, participantsMap, localUserId, localUserName, localNickname, doMasterPin, doUserPin, showSnackbar]);

  // ─── 더블클릭 핸들러 ─────────────────────────────────────────────────────
  const handleDoubleClick = useCallback((userId: string, isScreenShare?: boolean) => {
    dispatch(uiActions.setPresenterOverrideUserId(toPinKey(userId, isScreenShare)));
    dispatch(uiActions.setIsShareClick(false));
  }, [dispatch]);

  // ─── 접기/펼치기 핸들러 ────────────────────────────────────────────────────
  const handleToggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // ─── 핀 고정 핸들러 (MainVideoPanel에서 사용) ──────────────────────────────
  const handlePinUser = useCallback((userId: string, type: 'pin' | 'unpin', isScreenShare?: boolean) => {
    // 사용자 상호작용 시 일회성 플래그 클리어
    dispatch(uiActions.setIsShareClick(false));
    dispatch(uiActions.setPresenterOverrideUserId(null));
    const pinKey = toPinKey(userId, isScreenShare);
    const isMasterPinned = mainUser.masterPinnedUserIds.includes(pinKey);
    const isUserPinned = mainUser.pinnedUserIds.includes(pinKey);
    const isPinned = isMasterPinned || isUserPinned;

    // 고정 시 총 2개 초과 차단
    if (type === 'pin' && !isPinned) {
      const allPinned = new Set([
        ...mainUser.masterPinnedUserIds,
        ...mainUser.pinnedUserIds,
        pinKey,
      ]);
      if (allPinned.size > MAX_PRESENTER_COUNT) {
        showSnackbar('error', `화면은 최대 ${MAX_PRESENTER_COUNT}개까지 고정가능합니다.`);
        return;
      }
    }

    if (isLocalMaster) {
      if (type === 'pin' && !isMasterPinned) {
        // 마스터가 새로 고정 → "모두에게 / 나에게만" 선택
        const p = participantsMap[userId];
        const name = userId === localUserId
          ? (localUserName || localNickname || '')
          : (p?.displayName || p?.nickname || '');

        const dialogMessage = isScreenShare
          ? `${name}님의 공유 화면을\n모두에게 화면 고정 하시겠습니까?`
          : `${name}님을\n모두에게 화면 고정 하시겠습니까?`;

        LUXDialog.confirm(dialogMessage, {
          type: 'info',
          title: '화면 고정',
          confirmButtonLabel: '모두에게 화면 고정',
          cancelButtonLabel: '나에게만 화면 고정',
          confirmCallback: () => doMasterPin(pinKey),
          cancelCallback: () => doUserPin(pinKey),
        });
      } else if (isMasterPinned) {
        // 마스터 핀 해제
        doMasterUnpin(pinKey);
      } else {
        // 개인 핀 해제
        doUserUnpin(pinKey);
      }
    } else {
      // 비마스터
      if (type === 'unpin' && isMasterPinned) {
        showSnackbar('error', '마스터가 고정한 화면입니다.');
        return;
      }
      if (type === 'pin') {
        doUserPin(pinKey);
      } else {
        doUserUnpin(pinKey);
      }
    }
  }, [
    dispatch, localUserId, localUserName, localNickname, isLocalMaster,
    mainUser.masterPinnedUserIds, mainUser.pinnedUserIds,
    participantsMap, showSnackbar,
    doMasterPin, doUserPin, doMasterUnpin, doUserUnpin,
  ]);

  // ─── 프리뷰 삽입된 발표자 리스트 (v1 동일) ─────────────────────────────────
  const presenterListWithPreview = useMemo(() => {
    if (presenterPreviewIndex == null) return presenterList;
    // 자동 전환 모드 중 드래그 시: 프리뷰만 표시 (v1 동일)
    if (isUseAutoScreenSwitch) {
      return [{ id: '__DRAG_PREVIEW__', isLocal: false }];
    }
    return [
      ...presenterList.slice(0, presenterPreviewIndex),
      { id: '__DRAG_PREVIEW__', isLocal: false },
      ...presenterList.slice(presenterPreviewIndex),
    ];
  }, [presenterList, presenterPreviewIndex, isUseAutoScreenSwitch]);

  return (
    <div className="usermov_wrap usermov_wrapNew">
      <div id="presenter_screen_wrap" className={`user_mov customView anchorView${isExpanded ? ' anchorView--full' : ''}`}>
        {/* 청중 리스트 (수평 스크롤) */}
        <div id="audience_scroll_wrap" className="scrolled">
          {audienceList.map((user) => (
            <div
              key={user.isScreenShare ? `${user.id}-screen` : user.id}
              draggable
              onDragStart={(e) => handleDragStart(e, toPinKey(user.id, user.isScreenShare))}
              onDoubleClick={() => handleDoubleClick(user.id, user.isScreenShare)}
              style={{ cursor: 'grab' }}
            >
              <VideoPanel
                participant={user.isLocal ? 'local' : participantsMap[user.id]}
                localUserId={localUserId}
                isMain={false}
                isThumbnail={true}
                onSelect={user.isScreenShare
                  ? (id: string) => handleDoubleClick(id, true)
                  : handleDoubleClick}
                showScreenShare={user.isScreenShare}
              />
            </div>
          ))}
        </div>
        {/* 발표자 리스트 (드롭 영역) */}
        <div
          className={`sideView_wrap${presenterListWithPreview.length > 1 ? ' sideView_wrap--split' : ''}`}
          ref={presenterRef}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {/* 접기/펼치기 버튼 — v1: ExpandButton */}
          {audienceList.length > 0 && (
            <button type="button" className="sideView_wrap_foldBtn" onClick={handleToggleExpand}>
              <span className="blind">접기</span>
            </button>
          )}
          {presenterListWithPreview.map((user) => {
            if (user.id === '__DRAG_PREVIEW__') {
              return (
                <div key="drag-preview" className="item">
                  <button type="button" className="movbx" style={{ background: '#4a4a4a' }} />
                </div>
              );
            }
            const userPinKey = toPinKey(user.id, user.isScreenShare);
            const isPinnedByMaster = mainUser.masterPinnedUserIds.includes(userPinKey);
            const isPinnedByUser = mainUser.pinnedUserIds.includes(userPinKey);
            const isPinned = isPinnedByMaster || isPinnedByUser || (!user.isScreenShare && mainUser.pinnedUserId === user.id);
            return (
              <MainVideoPanel
                key={user.isScreenShare ? `${user.id}-screen` : user.id}
                participant={user.isLocal ? 'local' : participantsMap[user.id]}
                localUserId={localUserId}
                isUseAutoScreenSwitch={isUseAutoScreenSwitch}
                isPinned={isUseAutoScreenSwitch ? false : isPinned}
                hidePinButton={false}
                onPinUser={handlePinUser}
                onClick={() => onSelectParticipant?.(user.id)}
                onDoubleClick={() => {
                  handleDoubleClick(user.id, user.isScreenShare);
                  dispatch(uiActions.setIsShareClick(false));
                }}
                showScreenShare={user.isScreenShare}
              />
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default PresenterScreen;
